import { useCallback } from 'react';
import animateScrollTo from 'animated-scroll-to';
import { TIMEOUTS, testIds } from 'src/app/constants';

/**
 * Custom hook to handle smooth scroll over chat conversation
 */
function useSmoothScroll() {
  const container = document.getElementById(testIds.conversationSection);

  /**
   * Scroll Helpers
   */
  const querySelectNode = useCallback(
    (node: Element | undefined, dataId: string) =>
      node?.querySelector(`[data-test-id="${dataId}"]`) ?? false,
    []
  );

  const scrollX = useCallback(
    (node: Element, offset?: number) =>
      animateScrollTo(node, {
        verticalOffset: offset ?? 0,
        elementToScroll: container ?? undefined,
        speed: TIMEOUTS.AUTO_SCROLL_QUESTION_DELAY,
      }),
    [container]
  );

  /**
   * Scroll over nodes of conversation container
   * Find the node having explanation section or nudge and scroll to it.
   */

  const smoothScroll = useCallback(() => {
    // common elements
    const lastNode = container?.children[container.childElementCount - 1];
    const secondLastNode = container?.children[container.childElementCount - 2];
    const pinnedMessageOffset =
      document.getElementById(testIds.pinnedConversation)?.clientHeight ?? 0;

    // Spinner node scrolls to extreme bottom
    // TODO: Deprecate the spinner implementation for autoscroll to bottom
    if (lastNode?.id === testIds.spinner) {
      scrollX(lastNode, container?.scrollHeight);
      return false;
    }

    // More in Chat node scrolls to extreme bottom
    if (lastNode?.id === testIds.more_in_chat) {
      scrollX(lastNode, container?.scrollHeight);
      return false;
    }

    // Find lastest available nudge section
    const nudgeSection = querySelectNode(lastNode, testIds.nudge_section);
    if (nudgeSection) {
      scrollX(nudgeSection, container?.scrollHeight);
      // safari browser won't wait for css animation & will scroll anyways causing a jump
      nudgeSection.classList.add('animate-slideInUp');
      return true;
    }

    // Find lastest available explanation section
    const explanationSection =
      querySelectNode(lastNode, testIds.explanation_section) ||
      querySelectNode(secondLastNode, testIds.explanation_section);

    if (explanationSection) {
      scrollX(explanationSection, -pinnedMessageOffset);
      lastNode &&
        setTimeout(
          () => scrollX(lastNode, -pinnedMessageOffset),
          TIMEOUTS.AUTO_SCROLL_QUESTION_DELAY
        );

      return true;
    }

    // Nothing was found, scroll to top of chat bubble
    if (lastNode) {
      scrollX(lastNode, -pinnedMessageOffset);
    }

    return false;
  }, [container, querySelectNode, scrollX]);

  return [smoothScroll];
}

export default useSmoothScroll;
